import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, CalendarDays, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getDeliveryWindow, formatShortDate, toIsoDate } from '../utils/deliveryEstimate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Compute default expected delivery (uses pincode tier; defaults to metro 7 days)
  const computeDefaultDelivery = (pincode) => toIsoDate(getDeliveryWindow(pincode).maxDate);
  const computeMinDelivery = (pincode) => toIsoDate(getDeliveryWindow(pincode).minDate);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    shipping_address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    notes: '',
    gift_wrap: false,
    gift_message: '',
    expected_delivery_date: computeDefaultDelivery(),
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCart();
  }, [user]);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/cart`, {
        withCredentials: true,
      });
      if (!data.items || data.items.length === 0) {
        navigate('/cart');
        return;
      }
      setCart(data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!cart) return 0;
    // Prefer the discounted_subtotal from new dynamic cart; fall back to summing items
    if (typeof cart.discounted_subtotal === 'number') return cart.discounted_subtotal;
    if (!cart.items) return 0;
    return cart.items.reduce((total, item) => {
      const unit = item.discounted_unit_price ?? item.unit_price ?? item.product?.price ?? 0;
      return total + unit * item.quantity;
    }, 0);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'pincode') {
      const cleanPincode = value.replace(/\D/g, '').slice(0, 6);
      setFormData((prev) => ({
        ...prev,
        pincode: cleanPincode,
        // Auto-recompute expected delivery when a valid pincode is entered
        expected_delivery_date: cleanPincode.length === 6
          ? computeDefaultDelivery(cleanPincode)
          : prev.expected_delivery_date,
      }));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        shipping_address: formData.shipping_address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        phone: formData.phone,
        notes: formData.notes,
        gift_wrap: formData.gift_wrap,
        gift_message: formData.gift_wrap ? formData.gift_message : null,
        expected_delivery_date: formData.expected_delivery_date,
      };
      await axios.post(`${BACKEND_URL}/api/orders`, payload, { withCredentials: true });
      toast.success(formData.gift_wrap ? 'Order placed with gift wrap!' : 'Order placed successfully!');
      navigate(`/orders`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C27054] mx-auto mb-4"></div>
          <p className="text-[#7A8A73]">Loading checkout...</p>
        </div>
      </div>
    );
  }

  const subtotal = calculateTotal();
  const GST_RATE = 0.03;
  const gstAmount = Math.round(subtotal * GST_RATE * 100) / 100;
  const shipping = subtotal >= 1000 ? 0 : 50;
  const total = subtotal + gstAmount + shipping;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-6" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 md:mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">Final Step</p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1]">
            Checkout
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8"
            >
              <h2 className="text-2xl font-serif text-[#2D3B26] mb-6">Shipping Information</h2>

              <form onSubmit={handleSubmit} data-testid="checkout-form" className="space-y-5">
                {/* First Name + Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      data-testid="input-first-name"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      data-testid="input-last-name"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3B26] mb-2">
                    Address *
                  </label>
                  <textarea
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="input-address"
                    className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                    placeholder="House no, building, street, area"
                  />
                </div>

                {/* City + State + Pincode */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      data-testid="input-city"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      placeholder="e.g. Mumbai"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">State *</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      data-testid="input-state"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                    >
                      <option value="">Select State</option>
                      {['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh','Chandigarh','Puducherry','Andaman and Nicobar Islands','Dadra and Nagar Haveli and Daman and Diu','Lakshadweep'].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D3B26] mb-2">Pincode *</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{6}"
                      maxLength={6}
                      data-testid="input-pincode"
                      className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                      placeholder="6-digit pincode"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3B26] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                    maxLength={10}
                    data-testid="input-phone"
                    className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                    placeholder="10-digit mobile number"
                  />
                </div>

                {/* Expected Delivery Date */}
                <div>
                  <label className="block text-sm font-medium text-[#2D3B26] mb-2 flex items-center gap-1">
                    <CalendarDays size={14} className="text-[#C27054]" /> Expected Delivery Date *
                  </label>
                  <input
                    type="date"
                    name="expected_delivery_date"
                    value={formData.expected_delivery_date}
                    onChange={handleChange}
                    required
                    min={computeMinDelivery(formData.pincode)}
                    data-testid="input-expected-delivery"
                    className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                  />
                  {(() => {
                    const win = getDeliveryWindow(formData.pincode);
                    return (
                      <p className="text-[11px] text-[#7A8A73] mt-1.5 flex items-center gap-1.5" data-testid="delivery-helper-text">
                        <MapPin size={11} className="text-[#C27054]" />
                        {formData.pincode && formData.pincode.length === 6 ? (
                          <>
                            <span className="font-semibold text-[#475740]">{win.tier.label}:</span>
                            <span>{win.tier.minDays}–{win.tier.maxDays} working days</span>
                            <span>· Window: {formatShortDate(win.minDate)} – {formatShortDate(win.maxDate)}</span>
                          </>
                        ) : (
                          <span>Enter pincode for exact delivery window. Metro 5–7 days · Tier-2/3 8–10 days · Remote 10–12 days.</span>
                        )}
                      </p>
                    );
                  })()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3B26] mb-2">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={2}
                    data-testid="input-notes"
                    className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                    placeholder="Any special requests?"
                  />
                </div>

                {/* Gift Wrapping */}
                <div className="border border-[#C27054]/30 bg-[#fff5f0] rounded-xl p-5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.gift_wrap}
                      onChange={(e) => setFormData(prev => ({ ...prev, gift_wrap: e.target.checked }))}
                      data-testid="checkbox-gift-wrap"
                      className="mt-1 w-5 h-5 accent-[#C27054] cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Gift size={16} className="text-[#C27054]" />
                        <span className="text-sm font-semibold text-[#2D3B26]">Add Gift Wrapping</span>
                        <span className="text-[10px] uppercase tracking-wider bg-[#C27054] text-white px-2 py-0.5 rounded-full font-bold">FREE</span>
                      </div>
                      <p className="text-xs text-[#7A8A73] leading-relaxed">
                        Hand-tied with sage green ribbon &amp; an elegant NOVA seal. Perfect for surprises.
                      </p>
                    </div>
                  </label>

                  <AnimatePresence>
                    {formData.gift_wrap && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="mt-4 overflow-hidden"
                      >
                        <label className="block text-xs font-semibold text-[#2D3B26] uppercase tracking-wider mb-2">
                          Personal Message (Optional)
                        </label>
                        <textarea
                          name="gift_message"
                          value={formData.gift_message}
                          onChange={handleChange}
                          rows={3}
                          maxLength={200}
                          data-testid="input-gift-message"
                          className="w-full px-4 py-3 bg-white border border-[#E4E8DF] rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054] text-sm"
                          placeholder="e.g. Happy Birthday, with all my love..."
                        />
                        <p className="text-[10px] text-[#7A8A73] mt-1 text-right">
                          {formData.gift_message.length}/200
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="place-order-button"
                  className="w-full bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Processing...' : 'Place Order'}
                </button>
              </form>
            </motion.div>
          </div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-[#E4E8DF] rounded-2xl p-6 sticky top-32">
              <h2 className="text-xl font-serif text-[#2D3B26] mb-6">Order Summary</h2>

              {/* Cart Items */}
              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                {cart?.items?.map((item) => {
                  const lineUnit = item.discounted_unit_price ?? item.unit_price ?? item.product?.price ?? 0;
                  return (
                    <div key={`${item.product_id}-${item.karat || 'na'}`} className="flex gap-3">
                      {item.product && (
                        <>
                          <img
                            src={item.product.image_url}
                            alt={item.product.name}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-serif text-[#2D3B26] truncate">{item.product.name}</p>
                            <p className="text-xs text-[#7A8A73]">
                              {item.karat ? `${item.karat} · ` : ''}Qty: {item.quantity}
                            </p>
                            <p className="text-sm font-medium text-[#475740]">
                              ₹{(lineUnit * item.quantity).toLocaleString('en-IN')}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[rgba(45,59,38,0.1)] pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[#475740]">Subtotal</span>
                  <span className="font-medium text-[#2D3B26]" data-testid="checkout-subtotal">₹{subtotal.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#475740]">Shipping</span>
                  <span className="font-medium text-[#2D3B26]">{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
                </div>
                <div className="flex justify-between text-sm" data-testid="checkout-gst">
                  <span className="text-[#475740]">GST (3%)</span>
                  <span className="font-medium text-[#2D3B26]">₹{gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                {formData.gift_wrap && (
                  <div className="flex justify-between text-sm" data-testid="checkout-gift-wrap-line">
                    <span className="text-[#475740] flex items-center gap-1">
                      <Gift size={12} className="text-[#C27054]" /> Gift Wrap
                    </span>
                    <span className="font-medium text-green-700">Free</span>
                  </div>
                )}
                <div className="border-t border-[rgba(45,59,38,0.1)] pt-3 flex justify-between">
                  <span className="font-medium text-[#2D3B26]">Total</span>
                  <span className="text-xl font-serif text-[#C27054]" data-testid="checkout-total">₹{total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                {formData.expected_delivery_date && (
                  <div className="bg-[#E4E8DF] rounded-lg px-3 py-2 mt-2 flex items-center gap-2" data-testid="checkout-expected-delivery">
                    <CalendarDays size={14} className="text-[#C27054]" />
                    <div className="text-xs">
                      <p className="text-[#7A8A73] uppercase tracking-widest font-semibold text-[10px]">Expected Delivery</p>
                      <p className="text-[#2D3B26] font-medium">
                        {new Date(formData.expected_delivery_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 bg-[#FDFBF7] rounded-xl p-4">
                <p className="text-xs text-[#7A8A73] leading-relaxed">
                  Note: Payment integration will be added soon. Your order will be confirmed and we'll contact you for payment details.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;